<div id="register" class="login-card">
  <h2 class="login-card__title">
    {{ "login.registerCompanyTitle" | transloco | uppercase }}
  </h2>

  <!-- Single flat form per v2 mockup (no stepper): all fields visible at once -->
  <div [formGroup]="authForm">
    <!-- E-mail -->
    <div class="field" *ngIf="authForm.controls['email'] as email">
      <label class="field__label">{{ "login.email" | transloco }} <span class="field__req">*</span></label>
      <div class="field__input" [class.is-invalid]="email.touched && email.invalid">
        <mat-icon fontSet="material-icons-round">mail_outline</mat-icon>
        <input
          formControlName="email"
          placeholder="{{ 'login.emailEx' | transloco }}"
        />
      </div>
      <span class="field__error" *ngIf="email.touched && email.hasError('required')">{{
        "errors.required" | transloco
      }}</span>
      <span class="field__error" *ngIf="email.touched && email.hasError('email')">{{
        "errors.email" | transloco
      }}</span>
    </div>

    <!-- One address, one account. The same address cannot also be a customer account in the
         mobile app, and an address already in use anywhere in LOMY cannot be registered again. -->
    <div class="note register-account-scope-note">
      <mat-icon fontSet="material-icons-round">info_outline</mat-icon>
      <div>{{ "login.accountScopeNote" | transloco }}</div>
    </div>

    <!-- Manual-review mode: nudge toward an official company e-mail address -->
    <div class="note register-official-email-note" *ngIf="manualReview()">
      <mat-icon fontSet="material-icons-round">verified_user</mat-icon>
      <div>
        <strong>{{ "login.officialEmailTitle" | transloco }}</strong>
        {{ "login.officialEmailText" | transloco }}
      </div>
    </div>
  </div>

  <div [formGroup]="companyForm">
    <!-- OIB -->
    <div class="field" *ngIf="companyForm.controls['companyNo'] as companyNo">
      <label class="field__label">{{ "login.companyNo" | transloco }} <span class="field__req">*</span></label>
      <div
        class="field__input"
        [class.is-invalid]="companyNo.touched && companyNo.invalid"
      >
        <mat-icon fontSet="material-icons-round">business</mat-icon>
        <input formControlName="companyNo" />
      </div>
      <span
        class="field__error"
        *ngIf="companyNo.touched && companyNo.hasError('required')"
        >{{ "errors.required" | transloco }}</span
      >
      <span
        class="field__error"
        *ngIf="companyNo.touched && companyNo.hasError('pattern')"
        >{{ "errors.pattern" | transloco }}</span
      >
    </div>

    <!-- Phone -->
    <div class="field" *ngIf="companyForm.controls['phone1'] as phone">
      <label class="field__label">{{ "login.phone" | transloco }} <span class="field__req">*</span></label>
      <div
        class="field__input"
        [class.is-invalid]="phone.touched && phone.invalid"
      >
        <mat-icon fontSet="material-icons-round">call</mat-icon>
        <input
          formControlName="phone1"
          placeholder="{{ 'login.phoneEx' | transloco }}"
        />
      </div>
      <span
        class="field__error"
        *ngIf="phone.touched && phone.hasError('required')"
        >{{ "errors.required" | transloco }}</span
      >
      <span
        class="field__error"
        *ngIf="phone.touched && phone.hasError('pattern')"
        >{{ "errors.pattern" | transloco }}</span
      >
    </div>
  </div>

  <div [formGroup]="authForm">
    <!-- Password -->
    <div class="field" *ngIf="authForm.controls['password'] as password">
      <label class="field__label">{{ "login.password" | transloco }} <span class="field__req">*</span></label>
      <div
        class="field__input"
        [class.is-invalid]="password.touched && password.invalid"
      >
        <mat-icon fontSet="material-icons-round">lock_outline</mat-icon>
        <input
          placeholder="{{ 'login.passwordEx' | transloco }}"
          formControlName="password"
          [type]="passwordShown() ? 'text' : 'password'"
          (ngModelChange)="
            authForm.controls['passwordRetyped'].updateValueAndValidity()
          "
        />
        <mat-icon
          class="field__toggle"
          (click)="passwordShown.set(true)"
          *ngIf="!passwordShown()"
          fontSet="material-icons-round"
          >visibility_off</mat-icon
        >
        <mat-icon
          class="field__toggle"
          (click)="passwordShown.set(false)"
          *ngIf="passwordShown()"
          fontSet="material-icons-round"
          >visibility</mat-icon
        >
      </div>
      <span
        class="field__error"
        *ngIf="password.touched && password.hasError('required')"
        >{{ "errors.required" | transloco }}</span
      >
      <span
        class="field__error"
        *ngIf="password.touched && password.hasError('minlength')"
        >{{ "errors.minLength" | transloco }}</span
      >
    </div>

    <!-- Retype password -->
    <div
      class="field"
      *ngIf="authForm.controls['passwordRetyped'] as passwordRetyped"
    >
      <label class="field__label">{{
        "login.retypedPassword" | transloco
      }} <span class="field__req">*</span></label>
      <div
        class="field__input"
        [class.is-invalid]="passwordRetyped.touched && passwordRetyped.invalid"
      >
        <mat-icon fontSet="material-icons-round">lock_outline</mat-icon>
        <input
          formControlName="passwordRetyped"
          [type]="passwordRetypedShown() ? 'text' : 'password'"
        />
        <mat-icon
          class="field__toggle"
          (click)="passwordRetypedShown.set(true)"
          *ngIf="!passwordRetypedShown()"
          fontSet="material-icons-round"
          >visibility_off</mat-icon
        >
        <mat-icon
          class="field__toggle"
          (click)="passwordRetypedShown.set(false)"
          *ngIf="passwordRetypedShown()"
          fontSet="material-icons-round"
          >visibility</mat-icon
        >
      </div>
      <span
        class="field__error"
        *ngIf="passwordRetyped.touched && passwordRetyped.hasError('required')"
        >{{ "errors.required" | transloco }}</span
      >
      <span
        class="field__error"
        *ngIf="passwordRetyped.touched && passwordRetyped.hasError('password')"
        >{{ "errors.mismatch" | transloco }}</span
      >
    </div>
  </div>

  <div [formGroup]="companyForm">
    <!-- FINA registry down — manual company-data fallback -->
    <ng-container *ngIf="finaDown()">
      <div class="note note--warn register-fina-note">
        <mat-icon fontSet="material-icons-round">cloud_off</mat-icon>
        <div>
          <strong>{{ "login.finaDownTitle" | transloco }}</strong>
          {{ "login.finaDownText" | transloco }}
        </div>
      </div>

      <!-- Company name -->
      <div
        class="field"
        *ngIf="companyForm.controls['companyName'] as companyName"
      >
        <label class="field__label">{{
          "login.manualCompanyName" | transloco
        }} <span class="field__req">*</span></label>
        <div
          class="field__input"
          [class.is-invalid]="companyName.touched && companyName.invalid"
        >
          <mat-icon fontSet="material-icons-round">apartment</mat-icon>
          <input formControlName="companyName" />
        </div>
        <span
          class="field__error"
          *ngIf="companyName.touched && companyName.hasError('required')"
          >{{ "errors.required" | transloco }}</span
        >
      </div>

      <!-- Address -->
      <div class="field" *ngIf="companyForm.controls['address1'] as address1">
        <label class="field__label">{{
          "login.manualAddress" | transloco
        }} <span class="field__req">*</span></label>
        <div
          class="field__input"
          [class.is-invalid]="address1.touched && address1.invalid"
        >
          <mat-icon fontSet="material-icons-round">place</mat-icon>
          <input formControlName="address1" />
        </div>
        <span
          class="field__error"
          *ngIf="address1.touched && address1.hasError('required')"
          >{{ "errors.required" | transloco }}</span
        >
      </div>

      <!-- City -->
      <div class="field" *ngIf="companyForm.controls['city'] as city">
        <label class="field__label">{{ "login.manualCity" | transloco }} <span class="field__req">*</span></label>
        <div
          class="field__input"
          [class.is-invalid]="city.touched && city.invalid"
        >
          <mat-icon fontSet="material-icons-round">location_city</mat-icon>
          <input formControlName="city" />
        </div>
        <span
          class="field__error"
          *ngIf="city.touched && city.hasError('required')"
          >{{ "errors.required" | transloco }}</span
        >
      </div>

      <!-- Postal code -->
      <div
        class="field"
        *ngIf="companyForm.controls['postalCode'] as postalCode"
      >
        <label class="field__label">{{
          "login.manualPostalCode" | transloco
        }} <span class="field__req">*</span></label>
        <div
          class="field__input"
          [class.is-invalid]="postalCode.touched && postalCode.invalid"
        >
          <mat-icon fontSet="material-icons-round">markunread_mailbox</mat-icon>
          <input formControlName="postalCode" />
        </div>
        <span
          class="field__error"
          *ngIf="postalCode.touched && postalCode.hasError('required')"
          >{{ "errors.required" | transloco }}</span
        >
      </div>
    </ng-container>

    <!-- Checkbox -> terms of services and privacy policy -->
    <label class="login-card__terms">
      <mat-checkbox formControlName="checked"></mat-checkbox>
      <span>
        {{ "login.accept" | transloco }}
        <a
          class="accent-link"
          routerLink="../../terms-of-service"
          target="_blank"
          >{{ "login.tos" | transloco }}</a
        >
        {{ "login.andAgree" | transloco }}
        <a class="accent-link" routerLink="../../privacy-policy" target="_blank">
          {{ "login.privacy" | transloco }}</a
        >
      </span>
    </label>

    <app-button
      class="login-card__submit"
      [fullWidth]="true"
      text="{{ 'login.signUpHere' | transloco }}"
      [disabled]="authForm.invalid || companyForm.invalid || _auth.loading"
      (click)="register()"
    ></app-button>
  </div>

  <div class="login-card__footer">
    <p>
      {{ "login.alreadyHaveAccount" | transloco }}
      <a routerLink="../login" class="accent-link">{{
        "login.login" | transloco
      }}</a>
    </p>
  </div>
</div>
